/**
 * Touch arming: the hover state a finger cannot produce.
 *
 * A coarse pointer never hovers, so chrome that fades in on `group-hover`
 * has no way to appear. A HELD press ARMS the group instead — `data-armed`
 * on the group element — and the hover-reveal classes treat an armed group
 * exactly as a hovered one (`group-data-armed/<scope>:opacity-100`). One
 * finger, one ladder, read off how long it stays down:
 *
 *   release before ARM_HOLD_MS   the tap: the press, exactly as a click
 *   held to ARM_HOLD_MS          the group arms — wash paints, chrome shows,
 *                                and the click that follows the release is
 *                                SPENT on that reveal (nothing opens)
 *   held to DND_TOUCH_ACTIVATION the drag, where the group drags (dnd-kit's
 *                                touch activation sits just past the arm)
 *   held to the browser's 500ms  the context menu, where the group has one
 *
 * Arming holds until a press lands outside the armed element (a single
 * document-level `pointerdown` listener, installed on first use), so the
 * next tap can hit the revealed chrome. One branch is armed at a time:
 * arming disarms every armed element that is neither ancestor nor
 * descendant.
 *
 * The press that arms a group is remembered until the next finger goes down,
 * so every handler its release reaches — the group's own `onClick`, a cell's
 * press, a row's open, a checkbox's change — can ask `pressArmed` and treat
 * it as spent.
 *
 * Everything here keys on the POINTER (`pointerType`), never on viewport
 * width: a tablet at a desktop breakpoint still holds.
 */

/** Marks the element a held press highlighted. */
export const ARMED_ATTR = "data-armed";

/** Stamped by `Box hoverReveal={scope}`; its value is the scope. */
export const HOVER_REVEAL_ATTR = "data-hover-reveal";

/** Stamped beside `data-hover-reveal` by `Box revealOnTouch`: this chrome
 *  stands permanently on a coarse pointer, so it is never "hidden" there. */
export const REVEAL_ON_TOUCH_ATTR = "data-reveal-on-touch";

/**
 * How long a finger stays down before the group arms. Past a tap (a tap is
 * 80–150ms) and short of the browser's own 500ms long press, with room in
 * between for dnd-kit's touch activation (`DND_TOUCH_ACTIVATION`, 400ms):
 * hold, the chrome shows; keep holding, the card lifts.
 */
export const ARM_HOLD_MS = 300;

/** A finger that travels further than this before the hold completes is
 *  scrolling, not holding. Matches the tooltip press-reveal tolerance. */
const HOLD_TOLERANCE_PX = 10;

/**
 * A press belongs to a container (a row, a card, a div-press cell) only when
 * no descendant between the target and the container claimed it: native
 * interactive elements, ARIA button/checkbox surfaces (entity chips render
 * `role="button"`, never a nested `<button>`), and the div-click cells that
 * mark themselves with `data-row-click-stop`. This is what lets a row's
 * press coexist with the controls inside it instead of double-firing under
 * every one of them.
 */
export const PRESS_CLAIM_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [role="checkbox"], [data-row-click-stop]';

/**
 * Whether a click landing on `target` is a container's own press, or one a
 * descendant control already claimed. `false` for a target outside the
 * container's DOM — React bubbles a click in a portaled popup along the React
 * tree, so a pick inside a cell's picker reaches the cell's handler while
 * sitting nowhere inside it.
 */
export function isOwnPress(container: Element, target: EventTarget | null): boolean {
  if (!(target instanceof Node) || !container.contains(target)) return false;
  const element = target instanceof Element ? target : target.parentElement;
  const claimed = element?.closest(PRESS_CLAIM_SELECTOR);
  return !claimed || claimed === container || !container.contains(claimed);
}

/**
 * Whether a press came from a finger. A `pointerdown` says so outright on
 * every engine (`pointerType: "touch"`); a `click` does not — WebKit stamps
 * the synthetic click of a touch tap `"mouse"` — so the hold reads the
 * pointerdown and never the click. With no pointerType at all, fall back to
 * the device's pointer capability, the same media query `useIsTouchDevice`
 * uses.
 */
export function isTouchPress(event: Event | { nativeEvent: Event }): boolean {
  const native = "nativeEvent" in event ? event.nativeEvent : event;
  const pointerType =
    "pointerType" in native && typeof native.pointerType === "string"
      ? native.pointerType
      : "";
  if (pointerType !== "") return pointerType === "touch";
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none), (pointer: coarse)").matches
  );
}

// The elements the current press armed by holding. Everything that press
// releases into — the click, Base UI's relayed click on a hidden input, the
// change it reports — is spent, so this is keyed on the PRESS, not on one
// event object: it fills when a hold completes and empties on the next
// `pointerdown`, which is the only way a new press can begin.
const heldArmed = new Set<Element>();

let listening = false;
function listen(): void {
  if (listening || typeof document === "undefined") return;
  listening = true;
  // A press outside an armed element stands it down — anywhere on the page,
  // so a tap on another table, a toolbar or the nav clears the highlight.
  // Capture phase, so it runs before anything the press lands on.
  document.addEventListener(
    "pointerdown",
    (event) => {
      heldArmed.clear();
      const target = event.target instanceof Node ? event.target : null;
      document.querySelectorAll(`[${ARMED_ATTR}]`).forEach((armed) => {
        if (!target || !armed.contains(target)) armed.removeAttribute(ARMED_ATTR);
      });
    },
    true,
  );
}

/**
 * Arms `element`, disarming every other armed element outside its own
 * branch — neither an ancestor nor a descendant. Nested groups share a
 * press: a hold on a card inside a column arms both (the column's capture
 * runs first), and neither may stand the other down. Returns `true` when
 * the element was not armed before.
 */
export function arm(element: Element): boolean {
  listen();
  element.ownerDocument.querySelectorAll(`[${ARMED_ATTR}]`).forEach((other) => {
    if (
      other !== element &&
      !other.contains(element) &&
      !element.contains(other)
    ) {
      other.removeAttribute(ARMED_ATTR);
    }
  });
  if (element.hasAttribute(ARMED_ATTR)) return false;
  element.setAttribute(ARMED_ATTR, "");
  return true;
}

/** Stands `element` down. */
export function disarm(element: Element): void {
  element.removeAttribute(ARMED_ATTR);
}

/**
 * Starts the hold for a finger's `pointerdown` on `element`. Nothing
 * happens for a mouse. The hold completes after `ARM_HOLD_MS` unless the
 * finger lifts or travels first; completing arms the element and marks the
 * press spent (`pressArmed`) until the next finger goes down.
 */
export function beginHold(event: Event, element: Element): void {
  if (!isTouchPress(event)) return;
  listen();
  const start =
    event instanceof PointerEvent
      ? { x: event.clientX, y: event.clientY }
      : null;
  const doc = element.ownerDocument;
  let timer: number | undefined;

  const cleanup = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
    doc.removeEventListener("pointermove", onMove, true);
    doc.removeEventListener("pointerup", cleanup, true);
    doc.removeEventListener("pointercancel", cleanup, true);
  };
  const onMove = (move: PointerEvent) => {
    if (!start) return;
    if (
      Math.abs(move.clientX - start.x) > HOLD_TOLERANCE_PX ||
      Math.abs(move.clientY - start.y) > HOLD_TOLERANCE_PX
    ) {
      cleanup();
    }
  };
  doc.addEventListener("pointermove", onMove, true);
  doc.addEventListener("pointerup", cleanup, true);
  doc.addEventListener("pointercancel", cleanup, true);
  timer = window.setTimeout(() => {
    cleanup();
    arm(element);
    heldArmed.add(element);
  }, ARM_HOLD_MS);
}

/** Whether the current press is the one that armed `element` by holding —
 *  so any click it releases into is spent on the reveal, not a press. */
export function pressArmed(element: Element): boolean {
  return heldArmed.has(element);
}

/**
 * Whether the current press armed the nearest armed ancestor of `element`
 * by holding — for a handler inside the group (a name cell inside its row)
 * that never sees the group element itself.
 */
export function pressArmedClosest(element: Element): boolean {
  const armed = element.closest(`[${ARMED_ATTR}]`);
  return armed != null && heldArmed.has(armed);
}
